import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subMonths} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import Content from "./components/content.jsx";
import SosXrayService from "../../../services/reports/sos-xray-service.js";

const initialFilters = () => {
  const dateRef = new Date;
  return {
    dtStart: subMonths(dateRef, 1),
    dtEnd: dateRef,
    status: [],
    protectionType: [],
    analysis: { label: "Cadastros", value: "register" },
    providence: [],
  };
}

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  status: "Status",
  protectionType: "Programa de proteção",
  analysis: "Tipo de análise",
  providence: "Providência após acionamento",
};

const SosXRay = () => {
  const [loading, setLoading] = useState(false);

  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);

  const [filters, setFilters] = useState(initialFilters());
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [reportData, setReportData] = useState(null);

  const [title] = useState("Raio X de SOS");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const printComponentRef = useRef(null);

  useEffect(() => {
    if (filters.analysis?.value !== 'register' || !filters.providence?.length) return;

    setFilters(prev => ({...prev, providence: []}));
  }, [filters.analysis?.value, filters.providence?.length]);

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
      }, filterLabels)
    );

  }, [reportData]);

  const apply = async () => {
    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null);
      }
    });

      try {
        const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd);

        if (!periodValidation.ok) {
          ReportUtils.alertErrorMessage(periodValidation.message);
          setLoading(false);
          return;
        }

        const formattedFilters = {
          dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
          dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
          status: filters.status?.map(i => i.value) ?? [],
          protectionType: filters.protectionType?.map(i => i.value) ?? [],
          analysis: filters.analysis?.value ?? 'register',
          providence: filters.providence?.map(i => i.value) ?? []
        };

        const auditFilters = ReportUtils.filters({
            ...filters,
            dtStart: formattedFilters.dtStart,
            dtEnd: formattedFilters.dtEnd,
        }, filterLabels, true);

        const response = await SosXrayService.apply ({
          ...formattedFilters,
          filters: auditFilters
        });

        setReportData(response);
        setAppliedFilters(filters);

        const scale = window.visualViewport?.scale ?? 1;
        if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {
        ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
        console.error("Erro ao gerar o relatório", error);
    } finally {
        setLoading(false);
    }
  };

  useGlobalKey("Enter", apply);

  const clearSearch = () => {
    setFilters(initialFilters);
    setAppliedFilters(null);
    setReportData(null);
  }

  useGlobalKey("Escape", clearSearch);

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  const hasPositiveQuantity = (items) => Array.isArray(items) && items.some((item) => Number(item?.qty) > 0);

  const hasWeekdayData = (items) => Array.isArray(items) && items.some((item) =>
    ['domingo', 'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado'].some((day) => Number(item?.[day]) > 0),
  );

  const hasRegisterData = [
    reportData?.getRegistrationInfo?.status,
    reportData?.getRegistrationInfo?.citizen_history,
    reportData?.getRegistrationInfo?.life_risk,
    reportData?.getProtectionTypes,
    reportData?.getViolenceTypes,
    reportData?.getFamilyConnections,
    reportData?.getCitizensYearsOld,
    reportData?.getProtectedPlacesLocations,
  ].some(hasPositiveQuantity);

  const hasEventData = [
    Array.isArray(reportData?.getSosDetails) && reportData.getSosDetails.length > 0,
    Object.entries(reportData?.sosCards ?? {}).some(([key, value]) => key !== 'avg_dispatch_time' && Number(value) > 0),
    hasWeekdayData(reportData?.getAttendancesByWeekday),
    hasPositiveQuantity(reportData?.getProtectionByEvents),
    hasPositiveQuantity(reportData?.getEventProvidences),
  ].some(Boolean);

  const hasReportData = appliedFilters?.analysis?.value === 'event' ? hasEventData : hasRegisterData;

  return (
      <div className={"flex"}>
        <Template isLoading={loading}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={true}
                exportInSheets={true}
                printable={true}
                headerChange={true}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={hasReportData}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
                setCollapsed={setCollapsed}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch} minWidth={295}>

              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>

            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData || !hasReportData ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData || !hasReportData ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData || !hasReportData ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={!hasReportData && 0}/>
              <Content reportData={reportData} data={hasReportData} analysis={appliedFilters?.analysis?.value} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef}/>
            </div>
          </Card.Body>
        </Card>

      </div>
  );
};

export default SosXRay;